import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { AppUser, IAppUser } from '../models/app-user.model';

@Injectable({
  providedIn: 'root'
})
export class AppUserService {

  /* Estado del LISTADO. */
  public loading: boolean = false;
  public users: AppUser[] = [];
  public total: number = 0;

  /* Estado del DETALLE. Vive aparte del listado a propósito: la pantalla de
     detalle se abre sobre el listado ya cargado y pisar `loading`/`users`
     dejaría la tabla en blanco al volver. */
  public loadingUser: boolean = false;
  public user: AppUser | null = null;

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║
   ╚═════╝ ╚══════╝   ╚═╝

  */
  // region get
  /* A diferencia del resto de los `get` de la casa, este NO levanta el
     spinnerService: ese pinta un overlay que bloquea la pantalla completa. El
     detalle de usuario se dibuja con esqueletos por pieza, así que la carga se
     publica solo en `loadingUser`. */
  async get(
    uid: string
  ): Promise<AppUser | null> {
    this.loadingUser = true;
    this.user = null;

    const urlApi = `${environment.urlApi}/users/${encodeURIComponent(uid)}`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ user: IAppUser }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ user: IAppUser }>>(urlApi, {
            headers: {
              'content-type': 'application/json',
              'Authorization': token.toString()
            }
          }).subscribe({
            next: (response: IServerResponse<{ user: IAppUser }>) => resolve(
              new ServerResponse<{ user: IAppUser }>(response)
            ),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if (response.statusCode === 200) {
        this.user = new AppUser(response.data!.user);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      console.log(error);
    }

    this.loadingUser = false;

    return this.user;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  // region getAll
  async getAll(
    search: string,
    page: number,
    pageSize: number
  ): Promise<void> {
    this.loading = true;

    const params: string[] = [
      `page=${page}`,
      `pageSize=${pageSize}`
    ];

    /* El término va codificado: los usuarios se buscan por correo y un `+`
       sin codificar llega al backend como espacio. */
    if (search) params.push(`search=${encodeURIComponent(search)}`);

    const urlApi = `${environment.urlApi}/users?${params.join('&')}`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{
        users: IAppUser[];
        total: number;
        page: number;
        pageSize: number;
      }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{
            users: IAppUser[];
            total: number;
            page: number;
            pageSize: number;
          }>>(urlApi, {
            headers: {
              'content-type': 'application/json',
              'Authorization': token.toString()
            }
          }).subscribe({
            next: (response) => resolve(
              new ServerResponse<{
                users: IAppUser[];
                total: number;
                page: number;
                pageSize: number;
              }>(response)
            ),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if (response.statusCode === 200) {
        this.users = response.data!.users.map(
          item => new AppUser(item)
        );
        this.total = response.data!.total;
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      console.log(error);
    }

    this.loading = false;
  }

}
